// Centralized API fetch wrapper that injects environment headers.
// Usage: import { apiFetch, getClientEnv, setClientEnv } from './apiClient';

export type ClientEnvironment = 'DEV' | 'TEST' | 'PROD';

const ENV_HEADER_CANDIDATES = ['X-Client-Env', 'X-Env', 'X-Environment'] as const;

let cachedEnv: ClientEnvironment | undefined;

// Allow manual override via localStorage key or global variable.
export function setClientEnv(env: ClientEnvironment) {
  cachedEnv = env;
  try { localStorage.setItem('clientEnv', env); } catch { /* ignore */ }
}

export function getClientEnv(): ClientEnvironment {
  if (cachedEnv) return cachedEnv;
  // URL param override (?env=TEST)
  try {
    const params = new URLSearchParams(window.location.search);
    const qp = params.get('env');
    if (qp && ['DEV','TEST','PROD'].includes(qp.toUpperCase())) {
      cachedEnv = qp.toUpperCase() as ClientEnvironment;
      return cachedEnv;
    }
  } catch {/* ignore */}
  // localStorage
  try {
    const ls = localStorage.getItem('clientEnv');
    if (ls && ['DEV','TEST','PROD'].includes(ls)) {
      cachedEnv = ls as ClientEnvironment;
      return cachedEnv;
    }
  } catch {/* ignore */}
  // process env (build-time)
  if (process.env.REACT_APP_CLIENT_ENV && ['DEV','TEST','PROD'].includes(process.env.REACT_APP_CLIENT_ENV)) {
    cachedEnv = process.env.REACT_APP_CLIENT_ENV as ClientEnvironment;
    return cachedEnv;
  }
  // hostname heuristic
  const host = window.location.hostname.toLowerCase();
  if (host.includes('localhost') || host.startsWith('127.') || host.includes('0.0.0.0')) {
    cachedEnv = 'DEV';
  } else if (/test|pr|qa|stag/i.test(host)) {
    cachedEnv = 'TEST';
  } else {
    cachedEnv = 'PROD';
  }
  return cachedEnv;
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const env = getClientEnv();
  const headers = new Headers(init.headers || {});
  // Only add header if not already provided manually.
  const alreadyHas = ENV_HEADER_CANDIDATES.some(name => headers.has(name));
  if (!alreadyHas) {
    headers.set('X-Client-Env', env);
  }
  return fetch(input, { ...init, headers });
}

// Convenience JSON helper
export async function apiFetchJson<T = any>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(input, init);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json() as Promise<T>;
}
